import { useState } from 'react';
import { 
  Laptop, 
  Smartphone, 
  QrCode, 
  Sparkles, 
  ShieldCheck,
  ArrowRight
} from 'lucide-react';
import { SetupQrScannerModal } from './SetupQrScannerModal';

interface SetupMobileHandoffProps {
  onBypass: () => void;
  escolaNome?: string;
  progressoGeral?: number;
}

export function SetupMobileHandoff({
  onBypass,
  escolaNome = 'Sua Escola',
  progressoGeral = 0,
}: SetupMobileHandoffProps) {
  const [scannerOpen, setScannerOpen] = useState(false);

  return (
    <div 
      className="w-full min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 select-none overflow-y-auto"
      style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 42%, #3b82f6 100%)',
      }}
    >
      <div className="w-full max-w-md flex flex-col items-center gap-6 my-auto animate-fade-in">
        {/* Badge da Escola */}
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-xs shadow-md text-white">
          <Sparkles size={14} className="text-amber-300" />
          <span className="tracking-wide uppercase text-[11px] font-semibold">{escolaNome}</span>
          <span className="text-white/40">•</span>
          <span className="text-blue-100 font-medium">{progressoGeral}% Concluído</span>
        </div>

        {/* Card Principal com Fundo Branco */}
        <div className="w-full bg-white rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-100 flex flex-col text-slate-800">
          
          {/* Cabeçalho do Card */}
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4 shadow-inner">
              <Laptop size={28} />
            </div>

            <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
              Prefere configurar no computador?
            </h2>

            <p className="text-xs sm:text-sm text-slate-500 font-normal mt-2 leading-relaxed max-w-xs">
              A configuração de turmas, matérias e horários é mais prática e rápida em telas maiores.
            </p>
          </div>

          {/* Passos Limpos e Espaçados */}
          <div className="my-6 py-4 px-4 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col gap-3.5">
            <div className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-blue-600 text-white font-semibold flex items-center justify-center shrink-0 text-[11px] mt-0.5">
                1
              </span>
              <p className="text-xs sm:text-[13px] text-slate-600 font-normal leading-snug">
                No computador, acesse <span className="font-semibold text-blue-600">alunoconectado.com.br/login</span>
              </p>
            </div>

            <div className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-blue-600 text-white font-semibold flex items-center justify-center shrink-0 text-[11px] mt-0.5">
                2
              </span>
              <p className="text-xs sm:text-[13px] text-slate-600 font-normal leading-snug">
                Clique no botão abaixo para escanear o QR Code exibido na tela de login.
              </p>
            </div>
          </div>

          {/* Ações */}
          <div className="flex flex-col gap-2.5">
            <button
              type="button"
              onClick={() => setScannerOpen(true)}
              className="w-full py-3.5 px-4 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-semibold text-sm rounded-xl shadow-md flex items-center justify-center gap-2 transition-all active:scale-[0.99]"
            >
              <QrCode size={18} />
              <span>Escanear QR Code do Computador</span>
            </button>

            <button
              type="button"
              onClick={onBypass}
              className="w-full py-2.5 px-4 text-xs font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-50 rounded-xl transition-colors text-center"
            >
              Continuar configurando pelo celular
            </button>
          </div>
        </div>

        {/* Rodapé Seguro */}
        <div className="flex items-center justify-center gap-1.5 text-xs text-white/60 font-normal">
          <ShieldCheck size={14} className="text-emerald-300" />
          <span>Acesso rápido e seguro sem digitar senha</span>
        </div>
      </div>

      {/* Modal Scanner */}
      <SetupQrScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
      />
    </div>
  );
}
